import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, ExternalLink, MapPin } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { DESTINATIONS } from "../../data/cottages";
import { LangSwitch } from "./LangSwitch";
import { SafeImage } from "./SafeImage";
import { Gallery } from "./Gallery";
import { Lightbox } from "./Lightbox";
import { Sprig, Divider } from "./Ornaments";

const NEARBY = ["alv", "nas", "filmbyn", "fishing", "tourist"];

export const CottagePage = ({ cottage }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(null);
  const copy = t[cottage.key];
  const cover = cottage.photos[0];
  const coverCaption = t.captions[cover.caption];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = `${cottage.name} – Vimmerbyferie`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", copy.intro);
  }, [cottage.name, copy.intro]);

  return (
    <div className="cottage-page">
      <header className="cottage-page__header">
        <div className="wrap cottage-page__header-inner">
          <a href="/" className="cottage-page__brand" aria-label="Vimmerbyferie">
            <Sprig className="cottage-page__brand-sprig" />
            <span>
              <strong>Vimmerbyferie</strong>
              <small>{t.brandTag}</small>
            </span>
          </a>
          <div className="cottage-page__header-actions">
            <a href="/" className="cottage-page__back">
              <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
              {t.cottagePage.backHome}
            </a>
            <LangSwitch idPrefix="cottage-page" />
          </div>
        </div>
      </header>

      <main className="cottage-page__main">
        <section className="cottage-page__hero">
          <div className="wrap cottage-page__hero-grid">
            <div className="cottage-page__hero-copy">
              <p className="eyebrow">{t.cottagePage.eyebrow}</p>
              <p className="script cottage-page__tagline">{copy.tagline}</p>
              <h1>{cottage.name}</h1>
              <Divider />
              <p className="cottage-page__intro">{copy.intro}</p>
              <p className="cottage-page__address">
                <MapPin size={18} strokeWidth={1.4} aria-hidden="true" />
                {cottage.address}
              </p>
              <div className="cottage-page__hero-actions">
                <a className="btn btn--primary" href={cottage.booking[lang]} target="_blank" rel="noopener noreferrer">
                  {t.cottagePage.bookingButton}
                  <ExternalLink size={16} strokeWidth={1.5} aria-hidden="true" />
                </a>
                <a className="btn btn--outline" href="/#kontakt">{t.cottagePage.questionButton}</a>
              </div>
            </div>

            <button
              type="button"
              className="cottage-page__cover"
              onClick={() => setOpen(0)}
              aria-label={`${t.cottagePage.openCover}: ${coverCaption}`}
            >
              <SafeImage src={cover.src} alt={`${cottage.name} – ${coverCaption}`} loading="eager" decoding="async" />
              <span className="cottage-page__cover-caption">{coverCaption}</span>
            </button>
          </div>
        </section>

        <section className="cottage-page__facts-section">
          <div className="wrap cottage-page__facts-grid">
            <div>
              <p className="eyebrow">{t.cottagePage.verifiedTitle}</p>
              <h2>{cottage.name}</h2>
              <p className="cottage-page__facts-lead">{copy.intro}</p>
            </div>
            <ul className="cottage-page__facts">
              {copy.facts.map((fact, index) => {
                const Icon = cottage.icons[index];
                return (
                  <li key={fact}>
                    <Icon size={17} strokeWidth={1.4} aria-hidden="true" />
                    <span>{fact}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <section className="cottage-page__gallery-section">
          <div className="wrap">
            <div className="cottage-page__section-head">
              <p className="eyebrow">{t.cottagePage.galleryTitle}</p>
              <h2>{t.cottagePage.galleryTitle}</h2>
              <p>{t.cottagePage.galleryLead}</p>
            </div>
            <Gallery cottage={cottage} onOpen={setOpen} defaultExpanded />
          </div>
        </section>

        <section className="cottage-page__nearby">
          <div className="wrap">
            <div className="cottage-page__section-head">
              <p className="eyebrow">{t.cottagePage.nearbyEyebrow}</p>
              <h2>{t.cottagePage.nearbyTitle}</h2>
              <p>{t.cottagePage.nearbyLead}</p>
            </div>
            <div className="cottage-page__nearby-grid">
              {NEARBY.map((key) => {
                const dest = DESTINATIONS.find((item) => item.key === key);
                const item = t.activities.cards[key];
                return (
                  <a key={key} href={dest.url} target="_blank" rel="noopener noreferrer" className="cottage-page__nearby-card">
                    <span>{item.name}</span>
                    <p>{item.text}</p>
                    <small>{item.link} <ArrowUpRight size={14} strokeWidth={1.5} aria-hidden="true" /></small>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        <section className="cottage-page__booking">
          <div className="wrap">
            <div className="cottage-page__booking-card">
              <p className="script">{copy.tagline}</p>
              <h2>{t.cottagePage.bookingTitle}</h2>
              <p>{t.cottagePage.bookingText}</p>
              <div className="cottage-page__booking-actions">
                <a className="btn btn--primary" href={cottage.booking[lang]} target="_blank" rel="noopener noreferrer">
                  {t.cottagePage.bookingButton}
                  <ExternalLink size={16} strokeWidth={1.5} aria-hidden="true" />
                </a>
                <a className="btn btn--outline" href="/#kontakt">{t.cottagePage.questionButton}</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="cottage-page__footer">
        <div className="wrap">
          <a href="/">Vimmerbyferie</a>
          <span>{t.footer.note}</span>
        </div>
      </footer>

      {open !== null && (
        <Lightbox cottage={cottage} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
};
